import React from "react";
import { useDebounce } from "@tiny/utils";
import {
  useGetAlbertAudienceQuery,
  usePutSegmentFindFilterQuery,
  usePutSegmentFindIdsQuery,
} from "../../../../app/useGeneratedApi";
import { AudienceOption } from "../../TriggerForm.types";
import { AlbertAudience } from "../../../../app/GeneratedApi";
import { FORM_CONSTANTS } from "../SelectRules.constants";
import { UseFormSetValue } from "react-hook-form";
import { FormValues } from "../SelectRules.hooks";

interface UseAudienceDataParams {
  node: any;
  isTinyEmail: boolean;
  isTinyAlbert: boolean;
  setValue: UseFormSetValue<FormValues>;
  albertAudienceTypes?: string[];
  formFieldName?: string;
}

export const useAudienceData = ({
  node,
  isTinyEmail,
  isTinyAlbert,
  setValue,
  albertAudienceTypes = ["FORM"],
  formFieldName = "audiences",
}: UseAudienceDataParams) => {
  const [audienceSearchText, setSearchAudienceText] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [allAudiences, setAllAudiences] = React.useState<AudienceOption[]>([]);
  const [hasMoreResults, setHasMoreResults] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [filteredAlbertAudiences, setFilteredAlbertAudiences] = React.useState<
    AudienceOption[]
  >([]);

  const searchDebounced = useDebounce(audienceSearchText);

  // API queries
  const { data: audienceResponse, isFetching: isSegmentLoading } =
    usePutSegmentFindFilterQuery(
      {
        searchTextName: searchDebounced,
        segmentTypes: ["UPLOAD", "INTEGRATION"],
      },
      {
        page: currentPage,
        size: FORM_CONSTANTS.PAGE_SIZE,
        sort: ["updatedAt,DESC"],
        countAllCustomers: false,
      },
      {
        keepPreviousData: true,
        enabled: isTinyEmail,
      }
    );

  const { data: selectedAudiences } = usePutSegmentFindIdsQuery(
    {
      ids:
        node?.data?.audiences?.map((aud: any) => aud.id) ||
        node?.data?.segment_ids ||
        (node?.data?.condition?.conditions?.[1]?.conditions?.[0]
          ?.conditions?.[0]?.attributeType === "segment-activity" &&
          node?.data?.condition?.conditions?.[1]?.conditions?.[0]
            ?.conditions?.[0]?.criteria) ||
        [],
    },
    {
      enabled: !!(
        isTinyEmail &&
        (node?.data?.audiences?.length ||
          node?.data?.segment_ids?.length ||
          (node?.data?.condition?.conditions?.[1]?.conditions?.[0]
            ?.conditions?.[0]?.attributeType === "segment-activity" &&
            node?.data?.condition?.conditions?.[1]?.conditions?.[0]
              ?.conditions?.[0]?.criteria))
      ),
    }
  );

  const { data: albertAudiences, isLoading: isAlbertLoading } =
    useGetAlbertAudienceQuery(
      {
        sort: "updatedAt,DESC",
      },
      {
        enabled: isTinyAlbert,
      }
    );

  // Process TinyEmail audiences
  React.useEffect(() => {
    if (!isTinyEmail || !audienceResponse) return;

    const content = audienceResponse?.content ?? [];
    const newAudiences = content.map((segment: any) => ({
      id: segment.id || "",
      name: segment.name || "",
      type: segment.segmentType || "",
    })) as AudienceOption[];

    setAllAudiences((prev) => {
      const uniqueAudiences = new Map(
        (currentPage === 0 ? newAudiences : [...prev, ...newAudiences]).map(
          (audience) => [audience.id, audience]
        )
      );
      return Array.from(uniqueAudiences.values());
    });

    if (audienceResponse?.totalElements !== undefined) {
      setHasMoreResults(
        content.length === FORM_CONSTANTS.PAGE_SIZE &&
          audienceResponse.totalElements >
            (currentPage + 1) * FORM_CONSTANTS.PAGE_SIZE
      );
    }

    setIsLoadingMore(false);
  }, [audienceResponse, currentPage, isTinyEmail]);

  // Process TinyAlbert audiences
  React.useEffect(() => {
    if (!isTinyAlbert || !albertAudiences) return;

    const allAlbertAudiences = (albertAudiences.audiences || []).map(
      (audience: AlbertAudience) => ({
        id: audience.id || "",
        name: audience.name || "",
        type: audience.audienceType || "",
        numberOfCustomers: audience.numberOfCustomers,
      })
    ) as AudienceOption[];

    setAllAudiences(allAlbertAudiences);

    if (searchDebounced) {
      const filtered = allAlbertAudiences.filter((audience) =>
        audience.name.toLowerCase().includes(searchDebounced.toLowerCase())
      );
      setFilteredAlbertAudiences(filtered);
    } else {
      setFilteredAlbertAudiences(allAlbertAudiences);
    }
  }, [albertAudiences, searchDebounced, isTinyAlbert]);

  // Reset pagination on search
  React.useEffect(() => {
    if (isTinyEmail) {
      setCurrentPage(0);
      setHasMoreResults(true);
    }
  }, [searchDebounced, isTinyEmail]);

  // Set selected audiences in form
  React.useEffect(() => {
    if (
      isTinyEmail &&
      selectedAudiences?.segments &&
      selectedAudiences.segments.length > 0
    ) {
      setValue(
        formFieldName as any,
        selectedAudiences.segments.map((segment: any) => ({
          id: segment.id || "",
          name: segment.name || "",
          type: segment.type || "",
        })) as AudienceOption[]
      );
    } else if (isTinyAlbert) {
      const selectedIds =
        node?.data?.audiences?.map((a: any) => a.id) ||
        node?.data?.segment_ids ||
        (node?.data?.condition?.conditions?.[1]?.conditions?.[0]
          ?.conditions?.[0]?.attributeType === "segment-activity" &&
          node?.data?.condition?.conditions?.[1]?.conditions?.[0]
            ?.conditions?.[0]?.criteria) ||
        [];

      if (!selectedIds || selectedIds.length === 0) return;

      const selectedAlbertAudiences = (albertAudiences?.audiences || [])
        .filter((aud: AlbertAudience) => selectedIds.includes(aud.id))
        .map((aud: AlbertAudience) => ({
          id: aud.id,
          name: aud.name,
          type: aud.audienceType || "",
          numberOfCustomers: aud.numberOfCustomers,
        }));

      if (selectedAlbertAudiences.length > 0) {
        setValue(formFieldName as any, selectedAlbertAudiences);
      }
    }
  }, [
    selectedAudiences,
    albertAudiences,
    node,
    setValue,
    isTinyEmail,
    isTinyAlbert,
  ]);

  const handleAudienceSearch = (searchText: string) => {
    setSearchAudienceText(searchText);

    if (isTinyAlbert && allAudiences.length) {
      const filtered = allAudiences.filter((audience) =>
        audience.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredAlbertAudiences(filtered);
    }
  };

  const loadMoreAudiences = () => {
    if (isTinyEmail && hasMoreResults && !isLoadingMore && !isSegmentLoading) {
      setIsLoadingMore(true);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  return {
    audiences: isTinyAlbert ? filteredAlbertAudiences : allAudiences,
    handleAudienceSearch,
    loadMoreAudiences,
    isLoadingMore,
    hasMoreResults: isTinyEmail ? hasMoreResults : false,
    isLoading: isTinyEmail ? isSegmentLoading : isAlbertLoading,
  };
};
